import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

// Prefissi più rilevanti per Lignano (turisti UE) + selezione globale
export const COUNTRY_CODES = [
  { code: "IT", flag: "🇮🇹", name: "Italia", dial: "+39" },
  { code: "AT", flag: "🇦🇹", name: "Österreich", dial: "+43" },
  { code: "DE", flag: "🇩🇪", name: "Deutschland", dial: "+49" },
  { code: "CH", flag: "🇨🇭", name: "Schweiz", dial: "+41" },
  { code: "SI", flag: "🇸🇮", name: "Slovenija", dial: "+386" },
  { code: "HR", flag: "🇭🇷", name: "Hrvatska", dial: "+385" },
  { code: "HU", flag: "🇭🇺", name: "Magyarország", dial: "+36" },
  { code: "CZ", flag: "🇨🇿", name: "Česko", dial: "+420" },
  { code: "SK", flag: "🇸🇰", name: "Slovensko", dial: "+421" },
  { code: "PL", flag: "🇵🇱", name: "Polska", dial: "+48" },
  { code: "FR", flag: "🇫🇷", name: "France", dial: "+33" },
  { code: "ES", flag: "🇪🇸", name: "España", dial: "+34" },
  { code: "PT", flag: "🇵🇹", name: "Portugal", dial: "+351" },
  { code: "NL", flag: "🇳🇱", name: "Nederland", dial: "+31" },
  { code: "BE", flag: "🇧🇪", name: "België", dial: "+32" },
  { code: "UK", flag: "🇬🇧", name: "United Kingdom", dial: "+44" },
  { code: "IE", flag: "🇮🇪", name: "Ireland", dial: "+353" },
  { code: "DK", flag: "🇩🇰", name: "Danmark", dial: "+45" },
  { code: "SE", flag: "🇸🇪", name: "Sverige", dial: "+46" },
  { code: "NO", flag: "🇳🇴", name: "Norge", dial: "+47" },
  { code: "FI", flag: "🇫🇮", name: "Suomi", dial: "+358" },
  { code: "RO", flag: "🇷🇴", name: "România", dial: "+40" },
  { code: "BG", flag: "🇧🇬", name: "България", dial: "+359" },
  { code: "GR", flag: "🇬🇷", name: "Ελλάδα", dial: "+30" },
  { code: "US", flag: "🇺🇸", name: "United States", dial: "+1" },
];

const langToCountry = {
  it: "IT", en: "UK", de: "DE", pt: "PT", hu: "HU",
};

export const PhoneInput = ({ value, onChange, lang = "it", testId = "phone-input" }) => {
  // Default prefix based on current language
  const defaultCode = langToCountry[lang] || "IT";
  const [country, setCountry] = useState(() => COUNTRY_CODES.find((c) => c.code === defaultCode) || COUNTRY_CODES[0]);
  const [number, setNumber] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    // update parent with full number
    const clean = number.replace(/[^0-9]/g, "");
    onChange(clean ? `${country.dial}${clean}` : "");
  }, [country, number, onChange]);

  const filtered = COUNTRY_CODES.filter((c) =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.dial.includes(search) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex gap-2" ref={ref}>
      <div className="relative shrink-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testId}-country-btn`}
          className="h-10 flex items-center gap-1.5 rounded-md border border-[#1E3A5D]/25 bg-white px-3 hover:border-[#B59410] transition-colors"
        >
          <span className="text-base leading-none">{country.flag}</span>
          <span className="font-display text-xs text-[#1E3A5D] tracking-wider font-semibold">{country.dial}</span>
          <ChevronDown className={`w-3 h-3 text-[#1E3A5D] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div
            data-testid={`${testId}-country-menu`}
            className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl border border-[#1E3A5D]/15 shadow-xl z-50 overflow-hidden"
          >
            <div className="p-2 border-b border-[#1E3A5D]/10">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca paese o prefisso"
                data-testid={`${testId}-country-search`}
                className="w-full px-3 py-1.5 text-sm border border-[#1E3A5D]/15 rounded-md focus:outline-none focus:border-[#B59410]"
              />
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => { setCountry(c); setOpen(false); setSearch(""); }}
                  data-testid={`${testId}-country-option-${c.code}`}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                    c.code === country.code ? "bg-[#F5EFE4] text-[#1E3A5D]" : "text-[#1E3A5D] hover:bg-[#F5EFE4]"
                  }`}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="font-serif-body flex-1">{c.name}</span>
                  <span className="font-display text-xs text-[#B59410] font-semibold">{c.dial}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <p className="px-4 py-3 text-sm text-[#1E3A5D]/50 font-serif-body italic">Nessun risultato</p>
              )}
            </div>
          </div>
        )}
      </div>

      <input
        type="tel"
        inputMode="numeric"
        value={number}
        onChange={(e) => setNumber(e.target.value.replace(/[^0-9\s]/g, ""))}
        placeholder="123 456 7890"
        data-testid={`${testId}-number-input`}
        className="h-10 flex-1 min-w-0 px-3 rounded-md border border-[#1E3A5D]/25 bg-white text-[#1E3A5D] focus:outline-none focus:border-[#B59410] transition-colors"
      />
    </div>
  );
};
